import type {CustomDealHistoryItem, CustomDealInProgress} from './types';

import {Button, Divider, Loading} from '@ds/components';
import historyEmptyStateSvg from 'assets/images/promotion-center/new/historyEmptyState.svg';
import Empty from 'common/empty/Empty';
import HistoryItem from './components/common/HistoryItem/HistoryItem';
import {useEffect, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {getDoneCustomDeals, getInProgressCustomDeals} from 'store/slices/custom-deal';

import CustomDealLiveCard from './components/CustomDealLiveCard/CustomDealLiveCard';
import CustomDealQueuedCard from './components/CustomDealQueuedCard/CustomDealQueuedCard';
import CustomDealSection from './components/CustomDealSection/CustomDealSection';
import {CUSTOM_DEAL_HISTORY_STATE, CUSTOM_DEAL_PAGE_SIZE, CUSTOM_DEAL_ROUTES} from './constants';
import {formatDealDateRange, getCustomDealVendorId} from './utils';

const CustomDeal: React.FC = () => {
  const vendorId = getCustomDealVendorId();
  const [pagination, setPagination] = useState({page: 1, pageSize: CUSTOM_DEAL_PAGE_SIZE});
  const dispatch = useAppDispatch();
  const {doneDeals, inProgressDeals, loading} = useAppSelector((state) => state.customDeal);
  const navigate = useNavigate();
  const deals = (inProgressDeals ?? []) as CustomDealInProgress[];
  const historyItems = (doneDeals?.deals ?? []) as CustomDealHistoryItem[];
  const activeDeals = deals.filter((deal) => deal.state === 'active');
  const upcomingDeals = deals.filter((deal) => deal.state === 'initiated');

  useEffect(() => {
    void dispatch(getInProgressCustomDeals({vendorId}));
    void dispatch(
      getDoneCustomDeals({
        vendor_id: vendorId,
        page: pagination.page,
        page_size: pagination.pageSize
      })
    );
    // The first page is fetched once on mount, matching the previous list behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = () => {
    const nextPage = pagination.page + 1;
    setPagination({pageSize: pagination.pageSize, page: nextPage});
    void dispatch(
      getDoneCustomDeals({
        vendor_id: vendorId,
        page: nextPage,
        page_size: pagination.pageSize
      })
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-y-auto bg-white" id="customDealContainer">
      <div className="sticky top-0 z-10">
        <div className="bg-white px-9 py-8 shadow-md">
          <Button
            variant="fill"
            color="primary"
            size="lg"
            fullWidth
            onClick={() => navigate(CUSTOM_DEAL_ROUTES.selectProducts)}
          >
            ایجاد تخفیف جدید
          </Button>
        </div>
      </div>

      {activeDeals.length > 0 ? (
        <>
          <CustomDealSection title="تخفیف‌ فعال">
            {activeDeals.map((deal) => (
              <CustomDealLiveCard
                key={deal.dealId ?? deal.deal_id}
                productCount={deal.stock_availability?.product_count}
                date={formatDealDateRange(deal.start_date, deal.end_date)}
                stockAvailability={deal.stock_availability}
                onClick={() =>
                  navigate(CUSTOM_DEAL_ROUTES.detail.replace(':dealId', String(deal.deal_id)))
                }
              />
            ))}
          </CustomDealSection>
          <Divider className="bg-gray-02" size={8} />
        </>
      ) : null}

      {upcomingDeals.length > 0 ? (
        <>
          <CustomDealSection title="تخفیف در صف اجرا">
            {upcomingDeals.map((deal) => (
              <CustomDealQueuedCard
                key={deal.deal_id}
                productCount={deal.stock_availability?.product_count}
                date={formatDealDateRange(deal.start_date, deal.end_date)}
                startDate={deal.start_date}
                stockAvailability={deal.stock_availability}
                onClick={() =>
                  navigate(CUSTOM_DEAL_ROUTES.detail.replace(':dealId', String(deal.deal_id)))
                }
              />
            ))}
          </CustomDealSection>
          <Divider className="bg-gray-02" size={8} />
        </>
      ) : null}

      <h2 className="mx-4 mt-4 mb-2 text-subtitle-md text-neutral-fg">تاریخچه</h2>
      {historyItems.length ? (
        <InfiniteScroll
          dataLength={historyItems.length}
          scrollableTarget="customDealContainer"
          next={loadMore}
          hasMore={historyItems.length < (doneDeals?.total_items ?? 0)}
          loader={
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          }
          scrollThreshold={0.6}
        >
          <div className="flex flex-col">
            {historyItems.map((item) => (
              <HistoryItem
                item={item}
                key={item.id}
                title={item.title}
                subtitle={`${item.productCount} کالا`}
                stateTitle={CUSTOM_DEAL_HISTORY_STATE}
                state="done"
              />
            ))}
          </div>
        </InfiniteScroll>
      ) : (
        <Empty illustration={historyEmptyStateSvg} className="min-h-60 flex-1 gap-1 px-4 text-center">
          <p className="text-subtitle-md text-neutral-fg">هنوز تخفیفی ثبت نشده است</p>
          <p className="text-body-sm text-gray-34">
            تخفیف های تکمیل شده شما در اینجا نمایش داده می شود.
          </p>
        </Empty>
      )}
    </div>
  );
};

export default CustomDeal;
